import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Edit2,
  FileText,
  Plus,
  Trash2,
} from "lucide-react";
import { generateId, useApp, type ListingTemplate } from "@/context/AppContext";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/settings/listing-templates")({
  component: () => (
    <ManagerOnly>
      <TemplatesPage />
    </ManagerOnly>
  ),
});

const PLACEHOLDERS = [
  "{{generated}}",
  "{{cardName}}",
  "{{setName}}",
  "{{condition}}",
  "{{price}}",
  "{{variant}}",
  "{{rarity}}",
  "{{cardNumber}}",
];

const PLACEHOLDER_GUIDE = [
  {
    tag: "{{generated}}",
    description:
      "Auto-generated bundle description (same content as 'From Cards'). Use as the body of a template and wrap with your own intro/outro text.",
    example: "(multi-section bundle write-up)",
  },
  {
    tag: "{{cardName}}",
    description: "First card's name",
    example: "Charizard",
  },
  {
    tag: "{{setName}}",
    description: "First card's set name",
    example: "Base Set",
  },
  { tag: "{{condition}}", description: "Listing condition", example: "NM" },
  { tag: "{{price}}", description: "Listing price", example: "$12.50" },
  { tag: "{{variant}}", description: "First card's variant", example: "Holo" },
  {
    tag: "{{rarity}}",
    description: "First card's rarity",
    example: "Rare Holo",
  },
  {
    tag: "{{cardNumber}}",
    description: "First card's set number",
    example: "4/102",
  },
];

const EXAMPLE_TEMPLATE =
  "Welcome to my store!\n\n" +
  "{{generated}}\n\n" +
  "Ships in a penny sleeve + top loader within 24h. " +
  "Combined shipping on multi-buys — message with any questions.";

function TemplatesPage() {
  const { listingTemplates, addTemplate, updateTemplate, deleteTemplate } =
    useApp();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(true);

  const [draftName, setDraftName] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [nameError, setNameError] = useState("");

  const startNew = () => {
    setEditingId(null);
    setDraftName("");
    setDraftBody("");
    setNameError("");
    setShowNewForm(true);
    setConfirmDeleteId(null);
  };

  const startEdit = (tmpl: ListingTemplate) => {
    setShowNewForm(false);
    setEditingId(tmpl.id);
    setDraftName(tmpl.name);
    setDraftBody(tmpl.body);
    setNameError("");
    setConfirmDeleteId(null);
  };

  const cancelForm = () => {
    setShowNewForm(false);
    setEditingId(null);
    setDraftName("");
    setDraftBody("");
    setNameError("");
  };

  const saveTemplate = () => {
    if (!draftName.trim()) {
      setNameError("Template name is required");
      return;
    }
    if (editingId) {
      const existing = listingTemplates.find((t) => t.id === editingId);
      if (existing) {
        updateTemplate({
          ...existing,
          name: draftName.trim(),
          body: draftBody,
        });
      }
    } else {
      addTemplate({
        id: generateId("TMPL"),
        name: draftName.trim(),
        body: draftBody,
        createdAt: new Date().toISOString(),
      });
    }
    cancelForm();
  };

  const handleDelete = (id: string) => {
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      return;
    }
    deleteTemplate(id);
    setConfirmDeleteId(null);
    if (editingId === id) cancelForm();
  };

  const insertPlaceholder = (ph: string) => {
    setDraftBody((prev) => (prev + ph).slice(0, 4000));
  };

  const isFormOpen = showNewForm || editingId !== null;

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <Link
          to="/tools"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Tools
        </Link>
        {!isFormOpen && (
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
          >
            <Plus className="h-3.5 w-3.5" />
            New Template
          </button>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-foreground">
            Listing Templates
          </h1>
          <p className="text-sm leading-snug text-muted-foreground">
            Save reusable descriptions for your eBay listings. Use placeholders
            like <code className="font-semibold">{"{{cardName}}"}</code> to
            auto-fill listing details.
          </p>
        </div>

        {/* Guide panel */}
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <button
            type="button"
            onClick={() => setShowGuide((v) => !v)}
            className="flex w-full items-center justify-between gap-2 px-3.5 py-3"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-3.5 w-3.5 text-secondary" />
              <span className="text-sm font-semibold text-foreground">
                How to write a template
              </span>
            </span>
            {showGuide ? (
              <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
          {showGuide && (
            <div className="space-y-2.5 px-3.5 pb-3.5">
              <p className="text-xs leading-snug text-muted-foreground">
                Write your description using any text, then insert placeholders
                where you want card-specific info filled in automatically.
              </p>

              <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
                AVAILABLE PLACEHOLDERS
              </div>
              <div className="space-y-1.5">
                {PLACEHOLDER_GUIDE.map((item) => (
                  <div
                    key={item.tag}
                    className="flex items-center gap-2.5 border-t border-border pt-1.5 first:border-t-0 first:pt-0"
                  >
                    <code className="rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                      {item.tag}
                    </code>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium text-foreground">
                        {item.description}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        e.g. {item.example}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
                EXAMPLE TEMPLATE
              </div>
              <div className="rounded-md bg-muted p-3">
                <p className="text-[13px] leading-relaxed text-foreground">
                  {EXAMPLE_TEMPLATE}
                </p>
              </div>
              <p className="text-[11px] leading-snug text-muted-foreground">
                When applied to a listing, placeholders are replaced with that
                card's real values.
              </p>
            </div>
          )}
        </div>

        {/* Form card */}
        {isFormOpen && (
          <div className="space-y-3.5 rounded-md border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">
              {editingId ? "Edit Template" : "New Template"}
            </h2>

            <div className="space-y-1.5">
              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                TEMPLATE NAME <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={draftName}
                onChange={(e) => {
                  setDraftName(e.target.value);
                  setNameError("");
                }}
                placeholder='e.g. "Standard Single Card"'
                className={`w-full rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
                  nameError ? "ring-2 ring-red-500" : "focus:ring-primary/40"
                }`}
              />
              {nameError && (
                <p className="text-[11px] text-red-500">{nameError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                  DESCRIPTION BODY
                </label>
                <span className="text-[11px] text-muted-foreground">
                  {draftBody.length}/4000
                </span>
              </div>
              <textarea
                value={draftBody}
                onChange={(e) => setDraftBody(e.target.value.slice(0, 4000))}
                placeholder="Write your template body here…"
                rows={6}
                className="w-full resize-y rounded-md bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-medium tracking-wider text-muted-foreground">
                INSERT PLACEHOLDER
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => insertPlaceholder(ph)}
                    className="rounded-md bg-muted px-2.5 py-1 text-xs font-medium text-primary hover:bg-muted/80"
                  >
                    {ph}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={cancelForm}
                className="flex-1 rounded-md bg-muted px-3 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTemplate}
                className="flex-[2] rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                {editingId ? "Save Changes" : "Create Template"}
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {listingTemplates.length === 0 && !isFormOpen && (
          <div className="flex flex-col items-center gap-2.5 rounded-md border border-border bg-card p-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <div className="text-base font-semibold text-foreground">
              No Templates Yet
            </div>
            <p className="text-sm leading-snug text-muted-foreground">
              Tap "New Template" to create your first listing description
              template.
            </p>
          </div>
        )}

        {/* Template list */}
        {listingTemplates.map((tmpl) => {
          if (editingId === tmpl.id) return null;
          const isConfirmDelete = confirmDeleteId === tmpl.id;
          return (
            <div
              key={tmpl.id}
              className={`space-y-2 rounded-md border bg-card p-3.5 ${
                isConfirmDelete ? "border-destructive" : "border-border"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                  {tmpl.name}
                </div>
                <div className="flex items-center gap-1.5">
                  {isConfirmDelete ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="rounded-md bg-muted px-2.5 py-1 text-xs font-semibold text-muted-foreground"
                      >
                        Keep
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(tmpl.id)}
                        className="rounded-md bg-destructive px-2.5 py-1 text-xs font-semibold text-destructive-foreground"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => startEdit(tmpl)}
                        aria-label="Edit template"
                        className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(tmpl.id)}
                        aria-label="Delete template"
                        className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className="line-clamp-3 text-xs leading-snug text-muted-foreground">
                {tmpl.body || "(empty body)"}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
